import { BrazeEventsService } from './services/braze-events.service';
import {
  accountSummary$,
  amsState$,
  primaryNavOptions$,
  resetAmsStore,
  theme$,
  userInfo$,
  webToolItems$,
} from './repositories/ams/ams.repository';
import { CompanyType } from './models/company-type';
import { PrimaryNavKey } from './models/primary-nav-key';
import {
  userRoles$,
  isViewingChildAccount$,
  userAuthenticated$,
  userSwitchingAccounts$,
} from './repositories/account-manager/account-manager.repository';
import { hasPartnerRole } from './features/fleet-accounts/utils/has-partner-role';
import {
  ALK_PARTNER_ID,
  DEFAULT_PROFILE_SOURCE,
  DEFAULT_STAGING_PROFILE_SOURCE,
  TRIMBLE_BUCKET_PARTNER_REGEX,
} from './models/constants';
import {
  deregisterAppSettingsEffects,
  registerAppSettingsEffects,
} from './repositories/app-settings/app-settings.effects';
import {
  deregisterAmsEffects,
  registerAmsEffects,
} from './repositories/ams/ams.effects';
import { registerAmsFeaturesEffects } from './repositories/ams-features/ams-features.effects';
import {
  deregisterSecondaryNavEffects,
  registerSecondaryNavEffects,
} from './components/secondary-nav/repositories/secondary-nav.effects';
import {
  initBrazeInAppMessages,
  initializeAms,
  initPrimaryNavOptions,
  openBrazeSession,
  setTheme,
  switchAccountResetAms,
} from './repositories/ams/ams.actions';
import {
  isFleetPortalOnly,
  resetAppSettingsStore,
} from './repositories/app-settings/app-settings.repository';
import { resetSecondaryNavStore } from './components/secondary-nav/repositories/secondary-nav.repository';
import { subscribeToContentCards } from './repositories/content-cards/content-cards.actions';
import { filterOutLoadingState } from './utils/filter-out-loading-state';
import { amsFeaturesState$ } from './repositories/ams-features/ams-features.repository';
import { toast$ } from './repositories/toasts.repository';
import {
  initializeAppSettings,
  switchAccountResetAppSettings,
} from './repositories/app-settings/app-settings.actions';
import { OrderTrackingComponent } from './components/order-tracking/order-tracking.component';
import { SwitchAccountsComponent } from './components/switch-accounts/switch-accounts.component';
import { SupportChatComponent } from './components/support-chat/support-chat.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { BrazeCardViewerComponent } from './components/braze-card-viewer/braze-card-viewer.component';
import {
  deregisterAssetsEffects,
  registerAssetsEffects,
} from './repositories/assets/assets.effects';
import {
  deregisterVehiclesEffects,
  registerVehiclesEffects,
} from './repositories/vehicles/vehicles.effects';
import {
  deregisterDriversEffects,
  registerDriversEffects,
} from './repositories/drivers/drivers.effects';
import {
  deregisterContentCardsEffects,
  registerContentCardsEffects,
} from './repositories/content-cards/content-cards.effects';
import {
  deregisterVidyardEffects,
  registerVidyardEffects,
} from './repositories/vidyard/vidyard.effects';
import { resetContentCardsStore } from './repositories/content-cards/content-cards.repository';
import { reloadAssets } from './repositories/assets/assets.actions';
import {
  deregisterUserPreferencesEffects,
  registerUserPreferencesEffects,
} from './repositories/user-preferences/user-preferences.effects';
import {
  createAllTasksCompleteTourItinerary,
  createAssetsTourItinerary,
  createRoutingProfileTourItinerary,
  createVehicleGroupTourItinerary,
  createInviteDriversTourItinerary,
  createMapSetTourItinerary,
  createWelcomeTourItinerary,
} from './account-manager-tour';
import { ALK_ROLES } from './models/alk-roles';
import { PARTNER_ROLES } from './models/partner-roles';
import { AMS_ROLES } from './models/ams-roles';
import { composeRoles } from './utils/compose-roles';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { getCurrentAuthenticatedUser, logout } from '@tmx/auth';
import {
  APP_VERSION_TOKEN,
  InitializationState,
  SubscriptionManager,
} from '@tmx/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  translate,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import {
  ProfileDropdownComponent,
  WebToolItem,
  WebToolsDropdownComponent,
} from '@tmx/operandi';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, combineLatest } from 'rxjs';
import { dispatch } from '@ngneat/effects';
import {
  CONTENT_TOOLS,
  MAP_SURE,
  MAP_EXACT,
  PCM_WEB,
  ROUTE_REPORTER,
} from '@shared/core-utils';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import {
  AppShellComponent,
  ButtonComponent,
  FooterComponent,
  NavbarComponent,
  ThemeToggleComponent,
  SideNavComponent as MopSideNavComponent,
  SpinnerComponent,
  ToastOutletComponent,
  ToastService,
  AlertComponent,
  Theme,
  TourService,
  TourProgressComponent,
  AppShellService,
} from '@tmx/modus-operandi';
import { environmentProvider } from '@shared/environment-provider';

@Component({
  selector: 'lib-account-manager',
  imports: [
    TranslocoModule,
    SpinnerComponent,
    OrderTrackingComponent,
    SwitchAccountsComponent,
    SupportChatComponent,
    SideNavComponent,
    TooltipModule,
    WebToolsDropdownComponent,
    RouterLink,
    RouterOutlet,
    AsyncPipe,
    ProfileDropdownComponent,
    BrazeCardViewerComponent,
    ButtonComponent,
    AppShellComponent,
    NavbarComponent,
    MopSideNavComponent,
    FooterComponent,
    ThemeToggleComponent,
    ToastOutletComponent,
    ButtonComponent,
    AlertComponent,
    TourProgressComponent,
  ],
  templateUrl: './account-manager.component.html',
  styleUrl: './account-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountManagerComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly appVersion = inject(APP_VERSION_TOKEN);
  private readonly tourService = inject(TourService);
  private readonly appShellService = inject(AppShellService);
  private readonly sm = new SubscriptionManager();

  componentState = signal<InitializationState>('loading');
  appVersionText = signal(
    translate('accountManager.accountManager.appVersionText', {
      version: `${this.appVersion}`,
    })
  );

  showOrderTracking$ = primaryNavOptions$.pipe(
    map((options) => options.get(PrimaryNavKey.Accounts) !== null)
  );
  showSwitchAccounts$ = combineLatest([
    userRoles$,
    accountSummary$,
    isViewingChildAccount$,
  ]).pipe(
    map(([userRoles, accountSummary, isViewingChildAccount]) => {
      return (
        isViewingChildAccount ||
        ((hasPartnerRole(userRoles) ||
          userRoles.some((role) => role === 'Fleet Support')) &&
          (accountSummary.companyType === CompanyType.Partner ||
            accountSummary.partnerId === ALK_PARTNER_ID))
      );
    })
  );
  currentUser = signal(getCurrentAuthenticatedUser());

  userRoles = computed(() =>
    this.currentUser()
      .roles.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
      .join(', ')
  );
  profileImgSource = toSignal(
    userInfo$.pipe(
      map(({ picture }) => picture),
      map((source) => {
        // The default profile image does not look good in the UI so we set to an empty string to
        // use the fallback
        if (
          source === DEFAULT_STAGING_PROFILE_SOURCE ||
          source === DEFAULT_PROFILE_SOURCE
        ) {
          return '';
        }

        return source;
      })
    ),
    {
      initialValue: '',
    }
  );

  accountSummary = toSignal(accountSummary$);
  companyName = computed(() => this.accountSummary()?.companyName);
  companyId = computed(() => this.accountSummary()?.companyCode);
  partnerInfo = toSignal(
    accountSummary$.pipe(
      map(({ partnerName, partnerId }) => {
        if (partnerId === '' || partnerId === null) {
          return null;
        }

        if (TRIMBLE_BUCKET_PARTNER_REGEX.exec(partnerId)) {
          return null;
        }

        return partnerId === '' ? partnerName : `${partnerName} (${partnerId})`;
      })
    ),
    {
      initialValue: null,
    }
  );
  externalAccountId = computed(() => this.accountSummary()?.externalAccountId);

  webToolItems$ = webToolItems$.pipe(
    map((items) => items.map((item) => new WebToolItem(item)))
  );
  isViewingChildAccount$ = isViewingChildAccount$;

  currentYear = new Date().getFullYear();
  appShell = viewChild(AppShellComponent);

  constructor() {
    effect(() => {
      if (this.componentState() === 'initialized') {
        this.appShellService.setSideNavOpenState(true);

        const { roles } = getCurrentAuthenticatedUser();
        const alkRoles = isFleetPortalOnly() ? new Set<string>() : ALK_ROLES;
        const partnerRoles = isFleetPortalOnly()
          ? new Set<string>()
          : PARTNER_ROLES;
        const amsRoles = isFleetPortalOnly() ? new Set<string>() : AMS_ROLES;
        const hasAssetsRole = composeRoles(alkRoles, partnerRoles, amsRoles);

        const itineraryItems = [
          {
            tour: createWelcomeTourItinerary(this.tourService),
          },
          {
            tour: createAssetsTourItinerary(this.tourService),
            show: hasAssetsRole(roles),
          },
          {
            tour: createVehicleGroupTourItinerary(this.tourService),
          },
          {
            tour: createRoutingProfileTourItinerary(this.tourService),
          },
          {
            tour: createMapSetTourItinerary(this.tourService),
          },
          {
            tour: createInviteDriversTourItinerary(this.tourService),
            show: hasAssetsRole(roles),
          },
          {
            tour: createAllTasksCompleteTourItinerary(this.tourService),
          },
        ];

        this.tourService.register(
          itineraryItems
            .filter(({ show }) => show !== false)
            .map(({ tour }) => tour)
        );

        const navigationState = window.history.state as { startTour?: boolean };
        if (navigationState.startTour === true) {
          this.tourService.startTour();
        }
      }
    });
  }

  @HostListener('window:keydown.F5', ['$event'])
  onF5(event: KeyboardEvent) {
    if (environmentProvider.getEnvironment().name !== 'prod') {
      event.preventDefault();
      this.tourService.startTour();
    }
  }

  ngOnInit() {
    registerAppSettingsEffects();
    registerAmsEffects();
    registerAmsFeaturesEffects();
    registerSecondaryNavEffects();
    registerAssetsEffects();
    registerVehiclesEffects();
    registerDriversEffects();
    registerContentCardsEffects();
    registerVidyardEffects();
    registerUserPreferencesEffects();

    dispatch(initBrazeInAppMessages());
    dispatch(subscribeToContentCards());
    dispatch(openBrazeSession());

    dispatch(initializeAms());

    this.sm.manage = combineLatest([
      amsState$.pipe(filterOutLoadingState()),
      amsFeaturesState$.pipe(filterOutLoadingState()),
    ])
      .pipe(map((states) => states.every((state) => state === 'initialized')))
      .subscribe({
        next: (initialized) => {
          if (initialized) {
            this.componentState.set('initialized');
            dispatch(initPrimaryNavOptions());
          } else {
            this.componentState.set('failed');
          }
        },
      });

    this.sm.manage = toast$.subscribe({
      next: ({ type, content }) => {
        this.toastService.add({
          type: type ?? 'primary',
          content:
            typeof content === 'string'
              ? this.translocoService.translate(content)
              : '',
        });
      },
    });

    this.sm.manage = userAuthenticated$.subscribe({
      next: () => {
        dispatch(initializeAms());
        dispatch(reloadAssets());

        this.currentUser.set(getCurrentAuthenticatedUser());
      },
    });

    this.sm.manage = userSwitchingAccounts$.subscribe({
      next: () => {
        dispatch(switchAccountResetAppSettings());
        dispatch(switchAccountResetAms());
        this.componentState.set('loading');
      },
    });

    this.sm.manage = theme$.subscribe({
      next: (theme) => {
        this.brazeEventsService.logBrazeCustomEvents(
          theme === 'dark'
            ? this.brazeEventsService.DARK_MODE_ACTIVATED
            : this.brazeEventsService.LIGHT_MODE_ACTIVATED
        );
      },
    });
  }

  reInitialize() {
    dispatch(switchAccountResetAppSettings());
    dispatch(switchAccountResetAms());
    this.componentState.set('loading');

    dispatch(initializeAppSettings());
  }

  handleLogoClick() {
    this.router.navigateByUrl('/ams/loading');
  }

  openHelpUrl() {
    window.open(
      'https://learn.transportation.trimble.com/discover/result/?sub=LC/mappingroutingnavigation/accountmanager/onlinehelp/&tl=Account-Manager-Table-of-Contents&q=210204'
    );
    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.CLICKED_HELP_ICON
    );
  }

  handleWebToolsMenuClick() {
    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.CLICKED_WEBTOOLS_MENU
    );
  }

  handleWebToolItemClick(item: WebToolItem) {
    if (item.expired) {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_CONTENT_TOOLS
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_MAPSURE
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_MAPEXACT
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    } else if (item.licensed) {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_CONTENT_TOOLS
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_MAPSURE
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_MAPEXACT
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    } else {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_CONTENT_TOOLS
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_MAPSURE
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_MAPEXACT
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    }
  }

  logout() {
    logout();
  }

  handleThemeChanged(theme: Theme) {
    dispatch(setTheme({ theme }));
  }

  ngOnDestroy() {
    resetAppSettingsStore();
    deregisterAppSettingsEffects();

    resetAmsStore();
    deregisterAmsEffects();

    resetSecondaryNavStore();
    deregisterSecondaryNavEffects();

    deregisterAssetsEffects();
    deregisterVehiclesEffects();
    deregisterDriversEffects();

    deregisterContentCardsEffects();
    resetContentCardsStore();

    deregisterVidyardEffects();
    deregisterUserPreferencesEffects();

    this.sm.unsubscribe();
  }
}
